import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { seoConfig, SEOConfig } from '../config/seo.config';

@Injectable({
  providedIn: 'root'
})
export class SeoService {

  constructor(
    private meta: Meta, 
    private title: Title,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  updatePageSEO(routeName: string) {
    const config = seoConfig[routeName];
    if (!config) {
      console.warn(`SEO config not found for route: ${routeName}`);
      return;
    }

    // Update title
    this.title.setTitle(config.title);

    // Update meta tags
    this.updateBasicMetaTags(config);
    this.updateOpenGraphTags(config);
    this.updateTwitterTags(config);
    this.updateAdditionalTags(config);

    // Update structured data
    if (config.structuredData) {
      this.updateStructuredData(config.structuredData);
    }
  }

  private updateBasicMetaTags(config: SEOConfig) {
    this.meta.updateTag({ name: 'description', content: config.description });
    this.meta.updateTag({ name: 'keywords', content: config.keywords });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ name: 'author', content: 'Tamilarasi Technologies' });
    this.meta.updateTag({ name: 'viewport', content: 'width=device-width, initial-scale=1' });
    this.meta.updateTag({ httpEquiv: 'Content-Type', content: 'text/html; charset=utf-8' });
    
    // Canonical URL
    this.meta.updateTag({ rel: 'canonical', href: config.url });
  }

  private updateOpenGraphTags(config: SEOConfig) {
    this.meta.updateTag({ property: 'og:title', content: config.title });
    this.meta.updateTag({ property: 'og:description', content: config.description });
    this.meta.updateTag({ property: 'og:image', content: config.image });
    this.meta.updateTag({ property: 'og:url', content: config.url });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'DiGivCards' });
    this.meta.updateTag({ property: 'og:locale', content: 'en_US' });
  }

  private updateTwitterTags(config: SEOConfig) {
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:site', content: '@tamilarasitech' });
    this.meta.updateTag({ name: 'twitter:creator', content: '@tamilarasitech' });
    this.meta.updateTag({ name: 'twitter:title', content: config.title });
    this.meta.updateTag({ name: 'twitter:description', content: config.description });
    this.meta.updateTag({ name: 'twitter:image', content: config.image });
    this.meta.updateTag({ name: 'twitter:url', content: config.url });
  }

  private updateAdditionalTags(config: SEOConfig) {
    // Mobile app tags
    this.meta.updateTag({ name: 'mobile-web-app-capable', content: 'yes' });
    this.meta.updateTag({ name: 'apple-mobile-web-app-capable', content: 'yes' });
    this.meta.updateTag({ name: 'apple-mobile-web-app-status-bar-style', content: 'default' });
    this.meta.updateTag({ name: 'apple-mobile-web-app-title', content: 'DiGivCards' });
    
    // Theme color
    this.meta.updateTag({ name: 'theme-color', content: '#f84800' });
    this.meta.updateTag({ name: 'msapplication-TileColor', content: '#f84800' });
    
    // Additional SEO tags
    this.meta.updateTag({ name: 'application-name', content: 'DiGivCards' });
    this.meta.updateTag({ name: 'classification', content: 'Business, Technology, Networking' });
    this.meta.updateTag({ name: 'category', content: 'Business Tools' });
    this.meta.updateTag({ name: 'coverage', content: 'Worldwide' });
    this.meta.updateTag({ name: 'distribution', content: 'Global' });
    this.meta.updateTag({ name: 'rating', content: 'General' });
  }

  updateTitle(title: string) {
    this.title.setTitle(title);
  }

  updateMetaTags(config: {
    title?: string;
    description?: string;
    keywords?: string;
    image?: string;
    url?: string;
  }) {
    if (config.title) {
      this.updateTitle(config.title);
      this.meta.updateTag({ property: 'og:title', content: config.title });
      this.meta.updateTag({ name: 'twitter:title', content: config.title });
    }

    if (config.description) {
      this.meta.updateTag({ name: 'description', content: config.description });
      this.meta.updateTag({ property: 'og:description', content: config.description });
      this.meta.updateTag({ name: 'twitter:description', content: config.description });
    }

    if (config.keywords) {
      this.meta.updateTag({ name: 'keywords', content: config.keywords });
    }

    if (config.image) {
      this.meta.updateTag({ property: 'og:image', content: config.image });
      this.meta.updateTag({ name: 'twitter:image', content: config.image });
    }

    if (config.url) {
      this.meta.updateTag({ property: 'og:url', content: config.url });
      this.meta.updateTag({ name: 'twitter:url', content: config.url });
      this.meta.updateTag({ rel: 'canonical', href: config.url });
    }
  }

  updateStructuredData(data: any) {
    // Only run in browser environment, not during SSR
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Remove existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  }

  // Method to get SEO config for a specific route
  getSEOConfig(routeName: string): SEOConfig | null {
    return seoConfig[routeName] || null;
  }

  // Method to get all available route configs
  getAllConfigs(): Record<string, SEOConfig> {
    return seoConfig;
  }
}
